import { buildApiUrl, currentSchool } from '../config';

class AuthService {
  constructor() {
    // Staff list loaded from public/staff.json (or staff-demo.json for demo school) at runtime
    this.staffList = [];
    this.users = []; // User permissions loaded from localStorage
    this.schools = ["Sherborne Qatar", "Sherborne Doha", "Boys School", "Girls School", "BaniHajer School", "MOQ School"];
    this.userAssignments = {}; // Persistent user assignments
    this.schoolAssignments = {}; // Persistent school assignments
    this.initializationComplete = false;
    
    // Initialize data in the correct order
    this.initializationPromise = this.initialize();
  }

  // Initialize all data in the correct sequence
  async initialize() {
    try {
      // 🗑️ CRITICAL FIX: Clear old cache at initialization to force fresh load from backend
      // This prevents stale "Teachers" default from appearing after page refresh
      console.log('🗑️  Clearing old security groups cache at startup...');
      localStorage.removeItem('securityGroups');
      localStorage.removeItem('securityGroupsVersion');
      localStorage.removeItem('userSecurityGroups');
      localStorage.removeItem('userModules');
      
      // IMPORTANT: These must run in sequence, not parallel!
      // 1. Load staff and initialize default groups first
      await this.loadStaffAndUsers();
      
      // 1.5 Load custom security groups from backend (if any)
      await this.loadCustomSecurityGroups();
      
      // 2. Load and sync user assignments (overrides defaults)
      await this.loadUserAssignments();
      
      // 3. Load and sync school assignments
      await this.loadSchoolAssignments();
      
      this.initializationComplete = true;
      console.log('✅ AuthService fully initialized');
    } catch (error) {
      console.error('❌ AuthService initialization error:', error);
      this.initializationComplete = true; // Set to true anyway to prevent infinite waiting
    }
  }
  
  // Wait for initialization to complete before proceeding
  async ensureInitialized() {
    if (!this.initializationComplete) {
      await this.initializationPromise;
    }
  }

  // Load staff list and user permissions
  async loadStaffAndUsers() {
    // Use staff-demo.json for demo school, staff.json for production
    const staffFile = currentSchool === 'demo' ? 'staff-demo.json' : 'staff.json';
    console.log(`📋 Loading staff from: ${staffFile} (school: ${currentSchool})`);
    
    try {
      // Try loading from backend API first
      let response = await fetch(buildApiUrl(`/data/${staffFile}`));
      if (response.ok) {
        this.staffList = await response.json();
        console.log(`✅ Loaded staff list from backend (${staffFile}):`, this.staffList.length, 'staff members');
      } else {
        // Fallback to public folder if backend is not available
        console.log('⚠️ Backend not available, loading from public folder...');
        response = await fetch(`/${staffFile}`);
        if (response.ok) {
          this.staffList = await response.json();
          console.log(`✅ Loaded staff list from public folder (${staffFile}):`, this.staffList.length, 'staff members');
        } else {
          console.error('❌ Failed to load staff from both backend and public folder');
        }
      }
    } catch (error) {
      console.error('❌ Error loading staff list from backend, trying public folder...', error);
      try {
        // Fallback to public folder
        const response = await fetch(`/${staffFile}`);
        if (response.ok) {
          this.staffList = await response.json();
          console.log(`✅ Loaded staff list from public folder (fallback, ${staffFile}):`, this.staffList.length, 'staff members');
        } else {
          console.error('❌ Failed to load staff from public folder');
        }
      } catch (fallbackError) {
        console.error('❌ Error loading staff list from public folder:', fallbackError);
      }
    }

    // Load user permissions from localStorage
    this.loadUsers();
    
    // Initialize default security groups if not already done
    await this.initializeDefaultSecurityGroups();
    
    // Return staff list for external use
    return this.staffList;
  }

  // Load custom security groups from backend (if any have been customized)
  async loadCustomSecurityGroups() {
    try {
      console.log('🔄 Loading custom security groups from backend...');
      console.log('🌐 API URL:', buildApiUrl('/api/get-security-groups'));
      
      const response = await fetch(buildApiUrl('/api/get-security-groups'));
      
      if (!response.ok) {
        console.warn(`⚠️ Backend returned status ${response.status}`);
        return;
      }
      
      const data = await response.json();
      console.log('📥 Response from backend:', data);
      
      if (data.securityGroups) {
        const groupCount = Object.keys(data.securityGroups).length;
        console.log(`✅ Loaded ${groupCount} security groups from backend`);
        console.log('📋 Group names:', Object.keys(data.securityGroups));
        
        // Always save to localStorage, even if empty or partial
        this.setSecurityGroups(data.securityGroups);
      } else {
        console.warn('⚠️ No securityGroups field in response:', data);
      }
    } catch (error) {
      console.error('❌ Error loading custom security groups:', error);
      console.error('   Stack:', error.stack);
    }
  }

  // Load persistent user assignments from backend API (database)
  async loadUserAssignments() {
    try {
      console.log('🔄 Loading persistent user assignments from database...');
      
      // 🗑️ IMPORTANT: Clear any old cached assignments from localStorage first
      // This prevents stale data from interfering with fresh database data
      this.clearOldAssignmentCache();
      
      const response = await fetch(buildApiUrl('/api/get-assignments'));
      if (response.ok) {
        const data = await response.json();
        this.userAssignments = data.userSecurityGroups || {};
        console.log('✅ Loaded persistent user assignments from database:', this.userAssignments);
        console.log('📋 Users with persistent assignments:', Object.keys(this.userAssignments));
        console.log('💾 Storage location:', data.storage || 'database');
      } else {
        console.log('⚠️ Failed to load assignments from database, using empty assignments');
        this.userAssignments = {};
      }
    } catch (error) {
      console.error('❌ Error loading user assignments:', error);
      this.userAssignments = {};
    }
    
    // 🔄 Sync database assignments into localStorage security groups
    await this.syncJsonAssignmentsToSecurityGroups();
  }
  
  // Clear old assignment cache from localStorage
  clearOldAssignmentCache() {
    try {
      // These keys may contain stale data that conflicts with database
      const keysToCheck = ['userAssignments', 'userSecurityGroups'];
      
      keysToCheck.forEach(key => {
        if (localStorage.getItem(key)) {
          console.log(`🗑️ Clearing old cache: ${key}`);
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  // Sync persistent JSON assignments into localStorage security groups
  async syncJsonAssignmentsToSecurityGroups() {
    try {
      if (!this.userAssignments || Object.keys(this.userAssignments).length === 0) {
        console.log('📋 No JSON assignments to sync');
        return;
      }
      
      console.log('🔄 Syncing JSON assignments to security groups...');
      const securityGroups = this.getSecurityGroups();
      let changesMade = false;
      
      // For each user in JSON assignments
      for (const [email, targetGroup] of Object.entries(this.userAssignments)) {
        console.log(`🔍 Processing ${email} → ${targetGroup}`);
        
        // Remove user from all groups first
        let userFoundInGroups = false;
        for (const [groupName, group] of Object.entries(securityGroups)) {
          if (group.members && group.members.includes(email)) {
            if (groupName !== targetGroup) {
              // User is in wrong group, remove them
              group.members = group.members.filter(member => member !== email);
              console.log(`➖ Removed ${email} from ${groupName}`);
              changesMade = true;
            }
            userFoundInGroups = true;
          }
        }
        
        // Add user to correct group if not already there
        if (securityGroups[targetGroup]) {
          if (!securityGroups[targetGroup].members) {
            securityGroups[targetGroup].members = [];
          }
          if (!securityGroups[targetGroup].members.includes(email)) {
            securityGroups[targetGroup].members.push(email);
            console.log(`➕ Added ${email} to ${targetGroup}`);
            changesMade = true;
          }
        }
      }
      
      // Save changes if any were made
      if (changesMade) {
        this.setSecurityGroups(securityGroups);
        console.log('✅ Security groups synchronized with JSON assignments');
      } else {
        console.log('✅ Security groups already in sync with JSON assignments');
      }
      
    } catch (error) {
      console.error('❌ Error syncing JSON assignments:', error);
    }
  }

  // Load persistent school assignments from backend API (database)
  async loadSchoolAssignments() {
    try {
      console.log('🏫 Loading persistent school assignments from database...');
      
      // 🗑️ Clear old school assignment cache from localStorage
      if (localStorage.getItem('schoolAssignments')) {
        console.log('🗑️ Clearing old school assignments cache');
        localStorage.removeItem('schoolAssignments');
      }
      
      const response = await fetch(buildApiUrl('/api/get-school-assignments'));
      if (response.ok) {
        const data = await response.json();
        this.schoolAssignments = data.schoolAssignments || {};
        console.log('✅ Loaded persistent school assignments from database:', this.schoolAssignments);
        console.log('🏫 Users with school assignments:', Object.keys(this.schoolAssignments));
        console.log('💾 Storage location:', data.storage || 'database');
        
        // 🔄 Sync database school assignments into localStorage
        await this.syncJsonSchoolAssignmentsToLocalStorage();
      } else {
        console.log('⚠️ Failed to load school assignments from database, using empty school assignments');
        this.schoolAssignments = {};
      }
    } catch (error) {
      console.error('❌ Error loading school assignments:', error);
      this.schoolAssignments = {};
    }
  }

  // Sync persistent JSON school assignments into localStorage
  async syncJsonSchoolAssignmentsToLocalStorage() {
    try {
      if (!this.schoolAssignments || Object.keys(this.schoolAssignments).length === 0) {
        console.log('🏫 No JSON school assignments to sync');
        return;
      }
      
      console.log('🔄 Syncing JSON school assignments to localStorage...');
      
      // Get current localStorage school assignments
      const localStorageAssignments = JSON.parse(localStorage.getItem('schoolAssignments') || '{}');
      let changesMade = false;
      
      // Merge JSON assignments into localStorage (JSON is the source of truth)
      for (const [email, assignment] of Object.entries(this.schoolAssignments)) {
        const currentLocal = localStorageAssignments[email];
        
        // Check if assignment is different
        if (!currentLocal || 
            currentLocal.school !== assignment.school || 
            currentLocal.canManage !== assignment.canManage) {
          
          localStorageAssignments[email] = {
            school: assignment.school,
            canManage: assignment.canManage
          };
          console.log(`🏫 Synced ${email} → ${assignment.school} (canManage: ${assignment.canManage})`);
          changesMade = true;
        }
      }
      
      // Save to localStorage if changes were made
      if (changesMade) {
        localStorage.setItem('schoolAssignments', JSON.stringify(localStorageAssignments));
        console.log('✅ School assignments synchronized to localStorage');
      } else {
        console.log('✅ School assignments already in sync with localStorage');
      }
      
    } catch (error) {
      console.error('❌ Error syncing JSON school assignments:', error);
    }
  }

  // Save persistent user assignments to JSON file via backend API
  async saveUserAssignments() {
    try {
      console.log('💾 Saving user assignments to user-assignments.json...');
      console.log('   📋 this.userAssignments:', this.userAssignments);
      console.log('   📊 Assignments to save:', Object.keys(this.userAssignments || {}).length);
      
      // Make API call to your existing backend
      const response = await fetch(buildApiUrl('/api/update-assignments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userSecurityGroups: this.userAssignments || {}
        })
      });
      
      if (!response.ok) {
        console.error(`❌ HTTP error! status: ${response.status}`);
        const errorText = await response.text();
        console.error(`   Response: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ User assignments updated successfully:', result);
      
      // Show success message
      console.log('📋 Updated assignments:', this.userAssignments);
      
      return true;
    } catch (error) {
      console.error('❌ Error saving user assignments:', error);
      
      // Fallback to download method if backend is not available
      console.log('⚠️ Backend not available, falling back to download method...');
      return await this.saveUserAssignmentsDownload();
    }
  }

  // Fallback method: Download the JSON file (original implementation)
  async saveUserAssignmentsDownload() {
    try {
      console.log('💾 Generating user assignments download...');
      
      const assignmentData = {
        userSecurityGroups: this.userAssignments || {},
        version: "1.0",
        lastUpdated: new Date().toISOString(),
        description: "Persistent user security group assignments that work across all browsers"
      };
      
      // Create a downloadable JSON file
      const dataStr = JSON.stringify(assignmentData, null, 2);
      const dataBlob = new Blob([dataStr], {type: 'application/json'});
      
      // Create download link
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'user-assignments.json';
      
      // Auto-download the file
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('✅ User assignments file generated for download');
      
      // Show instruction to user
      alert(`📥 user-assignments.json downloaded!\n\n🔧 To apply changes:\n1. Replace the existing file in public/user-assignments.json\n2. Refresh the application\n\nThis ensures assignments work across all browsers.`);
      
      return true;
    } catch (error) {
      console.error('❌ Error generating download:', error);
      alert('Error generating user assignments file. Please try again.');
      return false;
    }
  }

  // Update user assignment and save to JSON
  async updateUserAssignment(email, groupName) {
    console.log(`🔄 Updating assignment: ${email} → ${groupName}`);
    
    // Update in memory
    if (!this.userAssignments) {
      this.userAssignments = {};
    }
    
    if (groupName === 'Teachers') {
      // Remove from assignments if assigning to default Teachers group
      delete this.userAssignments[email];
      console.log(`🗑️ Removed ${email} from persistent assignments (defaulting to Teachers)`);
    } else {
      this.userAssignments[email] = groupName;
      console.log(`✅ Added ${email} → ${groupName} to persistent assignments`);
    }
    
    // Auto-save the updated assignments
    await this.saveUserAssignments();
    
    return true;
  }

  // 🏫 SCHOOL ASSIGNMENT MANAGEMENT
  // Save school assignments to JSON file via backend API
  async saveSchoolAssignments(schoolAssignments) {
    try {
      console.log('🏫 Saving school assignments to school-assignments.json...');
      
      // Make API call to your existing backend
      const response = await fetch(buildApiUrl('/api/update-school-assignments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          schoolAssignments: schoolAssignments || {}
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ School assignments updated successfully:', result);
      
      return true;
    } catch (error) {
      console.error('❌ Error saving school assignments:', error);
      console.log('⚠️ Falling back to localStorage only...');
      return false; // Still save to localStorage even if API fails
    }
  }

  // Update individual school assignment
  async updateSchoolAssignment(email, schoolName, canManage = false) {
    console.log(`🏫 Updating school assignment: ${email} → ${schoolName}`);
    
    // Get current assignments from localStorage
    const schoolAssignments = JSON.parse(localStorage.getItem('schoolAssignments') || '{}');
    
    // Update or remove assignment
    if (schoolName === 'none' || schoolName === null) {
      delete schoolAssignments[email];
      console.log(`🗑️ Removed school assignment for ${email}`);
    } else {
      schoolAssignments[email] = {
        school: schoolName,
        canManage: canManage
      };
      console.log(`✅ Added ${email} → ${schoolName} (canManage: ${canManage})`);
    }
    
    // Save to localStorage
    localStorage.setItem('schoolAssignments', JSON.stringify(schoolAssignments));
    
    // Auto-save to backend
    await this.saveSchoolAssignments(schoolAssignments);
    
    return true;
  }

  // Login user - now works with email-based authentication from staff.json + permission data
  async login(email, password) {
    try {
      console.log('Login attempt for:', email);
      
      // 🔄 FORCE REFRESH: Clear cache and reload fresh data from backend on every login
      console.log('🔄 Forcing fresh data load from backend...');
      localStorage.removeItem('securityGroups');
      localStorage.removeItem('userSecurityGroups');
      
      // ⚠️ CRITICAL: Wait for all initialization to complete before proceeding
      // This ensures that loadUserAssignments() has completed and persistent
      // assignments from the database are loaded into this.userAssignments
      console.log('⏳ Waiting for AuthService initialization to complete...');
      await this.ensureInitialized();
      
      // 🔄 Force reload security groups from backend to get latest members
      console.log('🔄 Reloading security groups from backend...');
      await this.loadCustomSecurityGroups();
      
      console.log('✅ AuthService initialization complete, proceeding with login');
      
      // Find staff member by email
      const staffMember = this.staffList.find(staff => 
        staff.email.toLowerCase() === email.toLowerCase()
      );
      
      if (!staffMember) {
        console.log('Staff member not found in staff.json');
        return { success: false, message: 'User not found in staff directory' };
      }
      
      // Verify password against backend (supports per-user custom passwords)
      try {
        const verifyResponse = await fetch(buildApiUrl('/verify-password'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email, password: password })
        });
        const verifyResult = await verifyResponse.json();
        if (verifyResult.error) {
          // Backend DB not available — fallback to default password check
          console.warn('Password verify endpoint DB error, using fallback:', verifyResult.error);
          if (password !== 'Sh8rb0rne!') {
            console.log('Invalid password (fallback check after DB error)');
            return { success: false, message: 'Invalid password' };
          }
        } else if (!verifyResult.valid) {
          console.log('Invalid password (verified via backend)');
          return { success: false, message: 'Invalid password' };
        } else {
          console.log(`Password verified (custom: ${verifyResult.custom})`);
        }
      } catch (verifyErr) {
        // Backend not reachable — fallback to default password check
        console.warn('Password verify endpoint not available, using fallback:', verifyErr.message);
        if (password !== 'Sh8rb0rne!') {
          console.log('Invalid password (fallback check)');
          return { success: false, message: 'Invalid password' };
        }
      }
      
      // Ensure security groups are initialized
      await this.initializeDefaultSecurityGroups();
      
      // Get user's security group and permissions
      // Now this.userAssignments is guaranteed to be loaded from database
      const userSecurityGroup = this.getUserSecurityGroup(email);
      const userModules = this.getUserModulesFromSecurityGroup(email);
      const userPermissions = this.getUserPermissionsFromSecurityGroup(email);
      
      console.log(`User ${email} assigned to group: ${userSecurityGroup}`);
      console.log(`User modules:`, userModules);
      console.log(`User permissions:`, userPermissions);

      // Create session data
      const sessionUser = {
        email: staffMember.email,
        name: staffMember.name,
        school: staffMember.school,
        securityGroup: userSecurityGroup,
        modules: userModules,
        permissions: userPermissions,
        hrPermissions: userPermissions, // For backward compatibility
        isActive: true
      };

      // Update last login time
      userPermissions.lastLogin = new Date().toISOString();
      console.log('Login successful for:', sessionUser.name);

      // Store in localStorage
      localStorage.setItem('currentUser', JSON.stringify(sessionUser));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('staffPermissions', JSON.stringify(sessionUser));

      return { success: true, user: sessionUser };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'An error occurred during login' };
    }
  }

  // Office 365 login - similar to regular login but without password check
  async loginWithOffice365(email) {
    try {
      console.log('Office 365 login attempt for:', email);
      
      // 🔄 FORCE REFRESH: Clear cache and reload fresh data from backend
      console.log('🔄 Forcing fresh data load from backend...');
      localStorage.removeItem('securityGroups');
      localStorage.removeItem('userSecurityGroups');
      
      // ⚠️ CRITICAL: Wait for all initialization to complete
      console.log('⏳ Waiting for AuthService initialization to complete...');
      await this.ensureInitialized();
      
      // 🔄 Force reload security groups from backend
      console.log('🔄 Reloading security groups from backend...');
      await this.loadCustomSecurityGroups();
      
      console.log('✅ AuthService initialization complete, proceeding with Office 365 login');
      
      // Find staff member by email
      const staffMember = this.staffList.find(staff => 
        staff.email.toLowerCase() === email.toLowerCase()
      );
      
      if (!staffMember) {
        console.log('Staff member not found in staff.json');
        return { success: false, message: 'User not found in staff directory. Please contact IT support.' };
      }
      
      // Ensure security groups are initialized
      await this.initializeDefaultSecurityGroups();
      
      // Get user's security group and permissions
      const userSecurityGroup = this.getUserSecurityGroup(email);
      const userModules = this.getUserModulesFromSecurityGroup(email);
      const userPermissions = this.getUserPermissionsFromSecurityGroup(email);
      
      console.log(`Office 365 User ${email} assigned to group: ${userSecurityGroup}`);
      console.log(`User modules:`, userModules);
      console.log(`User permissions:`, userPermissions);

      // Create session data
      const sessionUser = {
        email: staffMember.email,
        name: staffMember.name,
        school: staffMember.school,
        securityGroup: userSecurityGroup,
        modules: userModules,
        permissions: userPermissions,
        hrPermissions: userPermissions,
        isActive: true,
        authMethod: 'office365'
      };

      // Update last login time
      userPermissions.lastLogin = new Date().toISOString();
      console.log('Office 365 login successful for:', sessionUser.name);

      // Store in localStorage
      localStorage.setItem('currentUser', JSON.stringify(sessionUser));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('staffPermissions', JSON.stringify(sessionUser));

      return { success: true, user: sessionUser };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Login failed' };
    }
  }

  // Logout user
  logout() {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('isAuthenticated');
  }

  // Get current user
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('currentUser');
      const isAuthenticated = localStorage.getItem('isAuthenticated');
      
      if (userStr && isAuthenticated === 'true') {
        return JSON.parse(userStr);
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
    return null;
  }

  // Check if user is authenticated
  isAuthenticated() {
    const user = this.getCurrentUser();
    return user !== null;
  }

  // Check if user has access to a specific module
  hasModuleAccess(moduleName) {
    const user = this.getCurrentUser();
    if (!user) return false;

    // Check security group access first
    const groupModules = this.getUserModulesFromSecurityGroup(user.email);
    if (groupModules.includes(moduleName)) {
      return true;
    }

    // Fallback to legacy permission system
    return user && Array.isArray(user.modules) && user.modules.includes(moduleName);
  }

  // Check if user has admin privileges (now checks if user can manage HR)
  isAdmin() {
    const user = this.getCurrentUser();
    if (!user) return false;

    // Check security group permissions first
    const groupPermissions = this.getUserPermissionsFromSecurityGroup(user.email);
    if (groupPermissions.includes('full-access') || groupPermissions.includes('full-system-access')) {
      return true;
    }

    // Fallback to legacy permission system
    return user && (
      user.hrPermissions?.canManage === true ||
      (Array.isArray(user.hrPermissions) && user.hrPermissions.includes('manage-staff'))
    );
  }

  // Check if user has HR access
  hasHRAccess() {
    return this.hasModuleAccess('hr');
  }

  // Check if user can manage HR settings
  canManageHR() {
    const user = this.getCurrentUser();
    if (!user) return false;

    // Check security group permissions first
    const groupPermissions = this.getUserPermissionsFromSecurityGroup(user.email);
    if (groupPermissions.includes('manage-staff') || groupPermissions.includes('full-access') || groupPermissions.includes('full-system-access')) {
      return true;
    }

    // Fallback to legacy permission system for backward compatibility
    return user && (
      user.hrPermissions?.canManage === true ||
      (Array.isArray(user.hrPermissions) && user.hrPermissions.includes('manage-staff'))
    );
  }

  // Check if user has access to specific school
  hasSchoolAccess(schoolName) {
    const user = this.getCurrentUser();
    if (!user || !this.hasHRAccess()) return false;
    
    // Check security group permissions first
    const groupPermissions = this.getUserPermissionsFromSecurityGroup(user.email);
    if (groupPermissions.includes('view-all-schools') || groupPermissions.includes('full-access') || groupPermissions.includes('full-system-access')) {
      return true;
    }

    // Legacy support for old structure
    if (Array.isArray(user.hrPermissions) && user.hrPermissions.includes('view-all-schools')) {
      return true;
    }
    if (user.hrPermissions?.schools === 'all') return true;
    if (Array.isArray(user.hrPermissions?.schools) && user.hrPermissions.schools.includes(schoolName)) {
      return true;
    }
    
    // Fallback: users can always see their own school
    return user.school === schoolName;
  }

  // Get all staff members for user management
  getAllStaff() {
    return this.staffList;
  }

  // Get all users with permissions (admin only)
  getAllUsers() {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !this.canManageHR()) {
      return [];
    }
    
    return this.users.map(user => ({
      id: user.id,
      email: user.email,
      name: this.staffList.find(staff => staff.email === user.email)?.name || 'Unknown',
      modules: user.modules,
      hrPermissions: user.hrPermissions,
      createdAt: user.createdAt,
      lastLogin: user.lastLogin,
      isActive: user.isActive
    }));
  }

  // Create new user permissions (admin only)
  async createUser(userData) {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !this.canManageHR()) {
      return { success: false, message: 'Unauthorized' };
    }

    try {
      // Check if staff member exists in staff.json
      const staffMember = this.staffList.find(staff => 
        staff.email.toLowerCase() === userData.email.toLowerCase()
      );
      
      if (!staffMember) {
        return { success: false, message: 'Staff member not found in staff directory' };
      }

      // Check if user permissions already exist
      const existingUser = this.users.find(u => u.email === userData.email);
      if (existingUser) {
        return { success: false, message: 'User permissions already exist' };
      }

      // Generate new ID
      const newId = this.users.length > 0 ? Math.max(...this.users.map(u => u.id)) + 1 : 1;

      // Create new user permissions
      const newUser = {
        id: newId,
        email: userData.email,
        password: userData.password || 'password123', // Default password
        modules: userData.modules || [], // e.g., ['hr', 'school-manager']
        hrPermissions: userData.hrPermissions || {
          schools: [],
          canManage: false
        },
        createdAt: new Date().toISOString(),
        lastLogin: null,
        isActive: true
      };

      // Add to users array
      this.users.push(newUser);
      console.log('User permissions added. Total users now:', this.users.length);

      // Save to localStorage
      this.saveUsers();
      console.log('User data saved to localStorage');

      return { success: true, user: newUser };
    } catch (error) {
      console.error('Create user error:', error);
      return { success: false, message: 'Failed to create user permissions' };
    }
  }

  // Update user (admin only)
  async updateUser(userId, userData) {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !this.isAdmin()) {
      return { success: false, message: 'Unauthorized' };
    }

    try {
      const userIndex = this.users.findIndex(u => u.id === userId);
      if (userIndex === -1) {
        return { success: false, message: 'User not found' };
      }

      // Update user data
      this.users[userIndex] = {
        ...this.users[userIndex],
        ...userData,
        id: userId // Ensure ID doesn't change
      };

      this.saveUsers();
      return { success: true, user: this.users[userIndex] };
    } catch (error) {
      console.error('Update user error:', error);
      return { success: false, message: 'Failed to update user' };
    }
  }

  // Delete user (admin only)
  async deleteUser(userId) {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !this.isAdmin()) {
      return { success: false, message: 'Unauthorized' };
    }

    // Don't allow deleting own account
    if (currentUser.id === userId) {
      return { success: false, message: 'Cannot delete your own account' };
    }

    try {
      const userIndex = this.users.findIndex(u => u.id === userId);
      if (userIndex === -1) {
        return { success: false, message: 'User not found' };
      }

      this.users.splice(userIndex, 1);
      this.saveUsers();
      return { success: true };
    } catch (error) {
      console.error('Delete user error:', error);
      return { success: false, message: 'Failed to delete user' };
    }
  }

  // Get user permissions by email
  getUserPermissions(email) {
    const user = this.users.find(u => 
      u.email.toLowerCase() === email.toLowerCase()
    );
    
    if (!user) {
      return null;
    }
    
    return {
      modules: user.modules || [],
      hrPermissions: user.hrPermissions || []
    };
  }

  // Update user permissions by email (admin only)
  async updateUserPermissions(email, modulePermissions, hrPermissionsList) {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !this.canManageHR()) {
      throw new Error('Unauthorized to update user permissions');
    }

    try {
      // Find user by email
      const userIndex = this.users.findIndex(u => 
        u.email.toLowerCase() === email.toLowerCase()
      );
      
      if (userIndex === -1) {
        // Create new user permissions if they don't exist
        const staffMember = this.staffList.find(staff => 
          staff.email.toLowerCase() === email.toLowerCase()
        );
        
        if (!staffMember) {
          throw new Error('Staff member not found in staff directory');
        }

        // Generate new ID
        const newId = this.users.length > 0 ? Math.max(...this.users.map(u => u.id)) + 1 : 1;

        // Create new user permissions
        const newUser = {
          id: newId,
          email: email,
          password: 'password123', // Default password - should be changed
          modules: modulePermissions || [],
          hrPermissions: hrPermissionsList || [],
          createdAt: new Date().toISOString(),
          lastLogin: null,
          isActive: true
        };

        this.users.push(newUser);
        console.log('Created new user permissions for:', email);
      } else {
        // Update existing user
        this.users[userIndex].modules = modulePermissions || [];
        this.users[userIndex].hrPermissions = hrPermissionsList || [];
        console.log('Updated user permissions for:', email);
      }

      this.saveUsers();
      return { success: true };
    } catch (error) {
      console.error('Update user permissions error:', error);
      throw error;
    }
  }

  // Get available schools
  getSchools() {
    return this.schools;
  }

  // Save users to localStorage
  saveUsers() {
    const updatedData = {
      users: this.users,
      schools: this.schools
    };
    
    // Store in staffPermissions key to distinguish from old hrUsersData
    localStorage.setItem('staffPermissions', JSON.stringify(updatedData));
  }

  // Load users from localStorage
  loadUsers() {
    try {
      // Try new key first, then fall back to old key for migration
      let storedData = localStorage.getItem('staffPermissions');
      
      if (!storedData) {
        // Migration: try old key
        storedData = localStorage.getItem('hrUsersData');
        if (storedData) {
          console.log('Migrating from old hrUsersData format...');
          // We'll need to transform the old data, but for now just use empty
          storedData = null;
        }
      }
      
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        this.users = parsedData.users || [];
        this.schools = parsedData.schools || this.schools;
        console.log('Loaded users from localStorage:', this.users.length, 'users');
      } else {
        // Initialize with empty user list if no data exists
        console.log('No localStorage data found, starting with empty user list');
        this.users = [];
        this.saveUsers(); // Save initial empty structure
      }
    } catch (error) {
      console.error('Error loading users from localStorage:', error);
      // If there's an error loading, start fresh
      this.users = [];
      this.saveUsers();
    }
  }

  // Security Groups Management - Load ONLY from PostgreSQL, no hardcoded defaults
  getDefaultSecurityGroups() {
    // Return empty object - security groups MUST come from PostgreSQL
    // This forces frontend to always fetch from backend
    return {};
  }

  getSecurityGroups() {
    try {
      const groups = localStorage.getItem('securityGroups');
      
      // If no groups in localStorage, return empty object (will be loaded from backend)
      if (!groups) {
        console.log('� No security groups in localStorage - will load from backend');
        return {};
      }
      
      return JSON.parse(groups);
    } catch (error) {
      console.error('Error loading security groups:', error);
      return {};
    }
  }

  setSecurityGroups(groups) {
    try {
      localStorage.setItem('securityGroups', JSON.stringify(groups));
      console.log('✅ Security groups saved to localStorage');
    } catch (error) {
      console.error('Error saving security groups:', error);
    }
  }

  // Force refresh security groups cache
  refreshSecurityGroupsCache() {
    console.log('🔄 Refreshing security groups cache...');
    localStorage.removeItem('securityGroups');
    localStorage.removeItem('securityGroupsVersion');
    localStorage.removeItem('userSecurityGroups'); // Clear any user-specific cache
    localStorage.removeItem('userModules'); // Clear user modules cache
    // Force reload of default groups
    const freshGroups = this.getDefaultSecurityGroups();
    this.setSecurityGroups(freshGroups);
    console.log('✅ Security groups cache refreshed with:', Object.keys(freshGroups));
    return freshGroups;
  }

  getUserSecurityGroup(email) {
    // ⚠️ WARNING: This is a synchronous method but initialization is async
    // In most cases, initialization should be complete by the time this is called
    // If not, we'll fall back to localStorage security groups
    if (!this.initializationComplete) {
      console.warn('⚠️  getUserSecurityGroup called before initialization complete - assignments may not be loaded yet');
    }
    
    // 🔒 HARDCODED ASSIGNMENTS (Override any other assignments)
    const hardcodedAssignments = {
      'hshehadeh@sherborneqatar.org': 'Super admin',
      'hibrahim@sherborneqatar.org': 'HR staff',
      // Add more hardcoded assignments here as needed
    };
    
    if (hardcodedAssignments[email]) {
      return hardcodedAssignments[email];
    }
    
    // 📋 Check persistent assignments from database
    if (this.userAssignments && this.userAssignments[email]) {
      return this.userAssignments[email];
    }
    
    const securityGroups = this.getSecurityGroups();
    console.log('🔐 Available groups:', Object.keys(securityGroups));
    
    // 🔍 Find which group the user belongs to (case-insensitive email matching)
    const emailLower = email.toLowerCase();
    for (const [groupName, group] of Object.entries(securityGroups)) {
      console.log(`🔍 Checking group "${groupName}" members:`, group.members);
      if (group.members && group.members.some(member => member.toLowerCase() === emailLower)) {
        console.log(`✅ Found user in group: ${groupName}`);
        return groupName;
      }
    }
    
    // ❌ REMOVED DEFAULT "Teachers" FALLBACK
    // If user is not found in any group after backend load completes,
    // return 'Teachers' only as a last resort for unassigned users
    console.log('⚠️ User not found in any group');
    console.log('💡 Make sure user is added to a security group in PostgreSQL');
    
    // Return Teachers only if explicitly needed, but log warning
    if (Object.keys(securityGroups).length === 0) {
      console.warn('⚠️ No security groups loaded from backend! Check database connection.');
    }
    
    return 'Teachers'; // Final fallback only if absolutely no groups exist
  }

  getUserModulesFromSecurityGroup(email) {
    console.log('🔍 Getting modules for email:', email);
    
    const groupName = this.getUserSecurityGroup(email);
    console.log('👥 User security group:', groupName);
    
    const securityGroups = this.getSecurityGroups();
    console.log('🔐 All security groups:', Object.keys(securityGroups));
    
    if (securityGroups[groupName]) {
      const modules = securityGroups[groupName].modules || [];
      console.log('📋 Group modules:', modules);
      return modules;
    }
    
    console.log('❌ No group found or no modules in group');
    return [];
  }

  getUserPermissionsFromSecurityGroup(email) {
    const groupName = this.getUserSecurityGroup(email);
    const securityGroups = this.getSecurityGroups();
    
    if (securityGroups[groupName]) {
      return securityGroups[groupName].permissions || [];
    }
    
    return [];
  }

  // Update staff security group
  async updateStaffSecurityGroup(email, groupName) {
    const securityGroups = this.getSecurityGroups();
    
    // Remove staff from all groups
    Object.keys(securityGroups).forEach(group => {
      if (securityGroups[group].members) {
        securityGroups[group].members = securityGroups[group].members.filter(member => member !== email);
      }
    });
    
    // Add to new group
    if (securityGroups[groupName]) {
      if (!securityGroups[groupName].members) {
        securityGroups[groupName].members = [];
      }
      securityGroups[groupName].members.push(email);
    }
    
    this.setSecurityGroups(securityGroups);
    
    // 🆕 AUTOMATICALLY UPDATE PERSISTENT ASSIGNMENTS
    await this.updateUserAssignment(email, groupName);
  }

  // Get school permissions for a user based on their security group
  async getSchoolPermissions(email) {
    try {
      console.log('🏫 Getting school permissions for:', email);
      
      // Make API call to get permissions from backend
      const response = await fetch(buildApiUrl(`/api/school-permissions/${encodeURIComponent(email)}`));
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ School permissions loaded:', result);
      
      return result.permissions;
    } catch (error) {
      console.error('❌ Error getting school permissions:', error);
      
      // Fallback to local logic if backend is not available
      return this.getSchoolPermissionsFallback(email);
    }
  }

  // Fallback method for school permissions
  getSchoolPermissionsFallback(email) {
    const userGroup = this.getUserSecurityGroup(email);
    console.log('🏫 Fallback school permissions for:', email, 'group:', userGroup);
    
    // Get school assignment from localStorage
    const schoolAssignments = JSON.parse(localStorage.getItem('schoolAssignments') || '{}');
    const userSchoolAssignment = schoolAssignments[email];
    const assignedSchool = userSchoolAssignment?.school;
    
    console.log('🏫 Assigned school from localStorage:', assignedSchool);
    
    if (userGroup === 'Super admin') {
      return {
        can_see_all_schools: true,
        assigned_school: null,
        restricted_to_school: false,
        description: "Super admin - full access to all schools"
      };
    } else if (assignedSchool === "All Schools") {
      // 🎯 ANY user assigned "All Schools" gets full access
      return {
        can_see_all_schools: true,
        assigned_school: "All Schools", 
        restricted_to_school: false,
        description: `${userGroup} with All Schools access`
      };
    } else if (assignedSchool) {
      // 🎯 ALL users (including HR staff) are restricted to their assigned school
      return {
        can_see_all_schools: false,
        assigned_school: assignedSchool,
        restricted_to_school: true,
        description: `${userGroup} restricted to ${assignedSchool}`
      };
    } else {
      // No school assigned - default behavior
      if (['HR staff', 'Finance', 'Operations'].includes(userGroup)) {
        // These roles get all schools by default if no specific assignment
        return {
          can_see_all_schools: true,
          assigned_school: null,
          restricted_to_school: false,
          description: `${userGroup} - default all schools access`
        };
      } else {
        // Other roles get no access if no assignment
        return {
          can_see_all_schools: false,
          assigned_school: null,
          restricted_to_school: true,
          description: `${userGroup} - no school assigned`
        };
      }
    }
  }

  // Initialize default security groups and assign all staff to Teachers
  async initializeDefaultSecurityGroups() {
    try {
      // Security groups are now managed via PostgreSQL only
      // Load them from backend on demand in loadCustomSecurityGroups()
      
      console.log('⏳ Loading security groups from PostgreSQL...');
      await this.loadCustomSecurityGroups();
      
      console.log('✅ Security groups initialized from database');
      
    } catch (error) {
      console.error('❌ Error initializing security groups:', error);
      // Not critical - groups will be loaded when needed
    }
  }
}

// Create singleton instance
const authService = new AuthService();

// Initialize the service (loads staff and user permissions)
authService.loadStaffAndUsers();

export default authService;